/**
 * Sorts selections by taxonomy, with priority items appearing first.
 *
 * Sorts the selections by the following criteria:
 * 1. First sorts all items by ID to maintain consistent order
 * 2. Groups items into three categories:
 *    - Priority items (taxonomy starts with: produce, meat, dairy)
 *    - Other taxonomy items (has l1TaxonomyName but not priority)
 *    - No taxonomy items (no l1TaxonomyName)
 * 3. Sorts priority items by their position in priority array (produce first, then meat, then dairy)
 * 4. Sorts other taxonomy items alphabetically by l1TaxonomyName
 * 5. No taxonomy items maintain their ID-sorted order
 *
 * @param {Array} selections - The array of selections to sort.
 * @returns {Array} The sorted array of selections.
 */
function sortSelectionsByTaxonomy(selections) {
    const priorityTaxonomies = ['produce', 'meat', 'dairy'];

    // First sort by ID to maintain consistent order
    const sortedById = selections.sort((a, b) => a.id - b.id);

    // Group items by their category
    const priorityItems = [];
    const otherTaxonomyItems = [];
    const noTaxonomyItems = [];

    sortedById.forEach((item) => {
        const taxonomy = item.l1TaxonomyName?.toLowerCase();

        if (!taxonomy) {
            noTaxonomyItems.push(item);
        } else if (priorityTaxonomies.some((priority) => taxonomy.startsWith(priority))) {
            priorityItems.push(item);
        } else {
            otherTaxonomyItems.push(item);
        }
    });

    // Sort priority items by their priority order
    priorityItems.sort((a, b) => {
        const aTaxonomy = a.l1TaxonomyName?.toLowerCase();
        const bTaxonomy = b.l1TaxonomyName?.toLowerCase();
        const aIndex = priorityTaxonomies.findIndex((priority) => aTaxonomy.startsWith(priority));
        const bIndex = priorityTaxonomies.findIndex((priority) => bTaxonomy.startsWith(priority));
        return aIndex - bIndex;
    });

    // Sort other taxonomy items alphabetically
    otherTaxonomyItems.sort((a, b) => {
        const aTaxonomy = a.l1TaxonomyName?.toLowerCase();
        const bTaxonomy = b.l1TaxonomyName?.toLowerCase();
        return aTaxonomy.localeCompare(bTaxonomy);
    });

    // Combine all groups in the desired order
    return [...priorityItems, ...otherTaxonomyItems, ...noTaxonomyItems];
}

export { sortSelectionsByTaxonomy };
